import { useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useUpdateVerificationPhotoMutation } from 'entities/User';

interface HookArgs {
  verificationPhotoId: string;
  onSuccess?: () => void;
}

interface HookApi {
  isLoading: boolean;
  isConfirmOpen: boolean;
  isRejectModalOpen: boolean;
  rejectReason: string;
  setRejectReason: (reason: string) => void;
  openConfirmModal: () => void;
  closeConfirmModal: () => void;
  openRejectModal: () => void;
  closeRejectModal: () => void;
  handleApprove: () => void;
  handleReject: () => void;
}

export const useManageVerificationPhoto = ({ verificationPhotoId, onSuccess }: HookArgs): HookApi => {
  const { t } = useAppTranslation(['profile', 'common']);

  const [updatePhoto, { isLoading }] = useUpdateVerificationPhotoMutation();
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isRejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const openConfirmModal = (): void => {
    setConfirmOpen(true);
  };

  const closeConfirmModal = (): void => {
    setConfirmOpen(false);
  };

  const openRejectModal = (): void => {
    setRejectModalOpen(true);
  };

  const closeRejectModal = (): void => {
    setRejectModalOpen(false);
  };

  const handleApprove = useCallback(async () => {
    try {
      await updatePhoto({
        verificationPhotoId,
        fileStatus: 'approved',
      }).unwrap();

      showNotification('info', t('Success', { ns: 'common' }), t('Document approved successfully'));
      onSuccess?.();
    } catch (err) {
      console.error(err);
      showNotification('error', t('Error', { ns: 'common' }), t('Error while approving document'));
    } finally {
      closeConfirmModal();
    }
  }, [verificationPhotoId, updatePhoto, t, onSuccess]);

  const handleReject = useCallback(async () => {
    try {
      await updatePhoto({
        verificationPhotoId,
        fileStatus: 'rejected',
        note: rejectReason,
      }).unwrap();

      showNotification('info', t('Success', { ns: 'common' }), t('Document rejected successfully'));
      onSuccess?.();
    } catch (err) {
      console.error(err);
      showNotification('error', t('Error', { ns: 'common' }), t('Error while rejecting document'));
    } finally {
      closeRejectModal();
      setRejectReason('');
    }
  }, [verificationPhotoId, rejectReason, updatePhoto, t, onSuccess]);

  return {
    isLoading,
    isConfirmOpen,
    isRejectModalOpen,
    rejectReason,
    setRejectReason,
    openConfirmModal,
    closeConfirmModal,
    openRejectModal,
    closeRejectModal,
    handleApprove,
    handleReject,
  };
};
